import { Button, Card, EmptyState, formatDate, formatTime, Icon, SegmentedControl, Skeleton } from "@helmr/ui";
import { Link } from "react-router-dom";
import { useState } from "react";
import { minutesOfDay } from "../../lib/dates";
import { useAppContext } from "../../app/permissions";
import { useAppointments, useFollowups, useHomeScope, type Range, type Scope } from "./hooks";
import { AttentionCard, BillsCard, CollectionsCard, MetricsCard, NotConnectedCard, RenewalsCard, ScheduleCard } from "./Panels";
import { WorkBand } from "./WorkBand";
import { home } from "./strings";
import s from "./home.module.css";

const t = home;

function greeting(sc: Scope) {
  const h = Math.floor(minutesOfDay(sc.nowMs, sc.ctx.timeZone) / 60);
  return h < 12 ? t.greeting.morning : h < 17 ? t.greeting.afternoon : t.greeting.evening;
}

function Hero({ sc, range, onRange }: { sc: Scope; range: Range; onRange: (r: Range) => void }) {
  const { ctx } = sc;
  const fu = useFollowups(sc);
  const ap = useAppointments(sc);
  const first = ctx.me?.staff.name.split(/\s+/)[0];
  const overdue = (fu.data?.items ?? []).filter((f) => f.overdue).length;
  const nowMin = minutesOfDay(sc.nowMs, ctx.timeZone);
  const items = ap.data?.items ?? [];
  const nextTrial = [...items].sort((a, b) => a.startsAt - b.startsAt).find((a) => a.kind === "Trial" && a.status === "booked" && minutesOfDay(a.startsAt, ctx.timeZone) >= nowMin);
  const left = items.filter((a) => a.status === "booked" && minutesOfDay(a.startsAt, ctx.timeZone) >= nowMin).length;
  const where = ctx.me ? `${ctx.me.business.name}${ctx.me.branch && sc.branchId ? `, ${ctx.me.branches.find((b) => b.id === sc.branchId)?.name ?? ctx.me.branch.name}` : ""}` : "";

  return (
    <div className={s.hero} aria-busy={ctx.status === "loading" || undefined}>
      <div>
        <p className={s.eyebrow}>{formatDate(sc.date, ctx.locale, "long")}{where ? ` · ${where}` : ""}</p>
        {ctx.status === "loading" ? <Skeleton width={220} height={22} style={{ marginTop: 6 }} /> : <h1 tabIndex={-1}>{greeting(sc)}{first ? `, ${first}` : ""}</h1>}
        <p className={s.sum} aria-live="polite">
          {fu.data ? <span><i className={`${s.dot} ${overdue ? s.crit : ""}`} />{overdue ? <b>{t.summary.overdue(overdue)}</b> : t.summary.noneOverdue}</span> : null}
          {ap.data ? <span><i className={`${s.dot} ${s.info}`} />{nextTrial ? <>{t.summary.nextTrial} <b>{nextTrial.person.name}</b> {t.summary.at} <b>{formatTime(nextTrial.startsAt, ctx.timeZone)}</b></> : t.summary.left(left)}</span> : null}
        </p>
      </div>
      <div className={s.heroRight}>
        <SegmentedControl label="Date range" value={range} onChange={onRange} options={[
          { value: "today", label: t.ranges.today },
          { value: "yesterday", label: t.ranges.yesterday },
          { value: "week" as Range, label: t.ranges.week, disabled: true, title: t.ranges.weekDisabled },
        ]} />
        <div className={s.qa}>
          {ctx.has("leads.edit") ? <Button as={Link} to="/leads/trial">{t.quick.trial}</Button> : null}
          {ctx.has("billing.create_bill") ? <Button as={Link} to="/billing">{t.quick.payment}</Button> : null}
          {ctx.has("members.add") ? <Button as={Link} to="/members" variant="primary" className={s.newMember}><Icon name="plus" />{t.quick.member}</Button> : null}
        </div>
      </div>
    </div>
  );
}

export function HomePage() {
  const sc = useHomeScope();
  const ctxFull = useAppContext();
  const [range, setRange] = useState<Range>("today");
  if (ctxFull.status === "error") return <Card><EmptyState icon="alert" title="Could not load your profile" description="Check your connection and try again." action={<Button onClick={ctxFull.retry}>{t.states.retry}</Button>} /></Card>;
  return (
    <div className={s.page}>
      <title>Home · Helmr</title>
      <Hero sc={sc} range={range} onRange={setRange} />
      <WorkBand sc={sc} />
      <MetricsCard sc={sc} range={range} />
      <NotConnectedCard title={t.target.title} what={t.target.what} />
      <AttentionCard sc={sc} />
      <div className={s.two}>
        <ScheduleCard sc={sc} />
        <NotConnectedCard title={t.floor.title} what={t.floor.what} />
      </div>
      <CollectionsCard sc={sc} range={range} />
      <div className={s.one}><RenewalsCard sc={sc} /></div>
      <div className={s.two}>
        <BillsCard sc={sc} />
        <NotConnectedCard title={t.staffPay.title} what={t.staffPay.what} />
      </div>
    </div>
  );
}

